// ============================================================
// C2：高清出图工具（分辨率预设 / 视角预设 / 文件名 / 水印 / 下载）
// ============================================================

export interface ExportPreset {
  key: string;
  label: string;
  width: number;
  height: number;
}

export const EXPORT_PRESETS: ExportPreset[] = [
  { key: 'fhd', label: '1080p（1920×1080）', width: 1920, height: 1080 },
  { key: 'qhd', label: '2K（2560×1440）', width: 2560, height: 1440 },
  { key: 'uhd', label: '4K（3840×2160）', width: 3840, height: 2160 },
];

export interface ExportView {
  key: string;
  label: string;
  position?: [number, number, number];
  target?: [number, number, number];
}

export const EXPORT_VIEWS: ExportView[] = [
  { key: 'current', label: '当前视角' },
  { key: 'perspective', label: '透视', position: [20, 14, 20], target: [0, 0, 0] },
  { key: 'top', label: '俯视', position: [0, 50, 0.01], target: [0, 0, 0] },
  { key: 'front', label: '前视', position: [0, 10, 50], target: [0, 0, 0] },
  { key: 'side', label: '侧视', position: [50, 10, 0], target: [0, 0, 0] },
];

export function buildExportFilename(sceneName: string, width: number, height: number, format: 'png' | 'jpeg'): string {
  const safe = String(sceneName || '场景')
    .replace(/[\\/:*?"<>|\s]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40) || '场景';
  return `${safe}-${width}x${height}.${format === 'jpeg' ? 'jpg' : 'png'}`;
}

/** 免费/水印档：给导出图叠加水印（斜纹文字 + 左下角标识） */
export async function stampWatermark(dataUrl: string, opts: { text?: string; logoUrl?: string } = {}): Promise<string> {
  const text = opts.text ?? 'DTwince';
  const img = await loadImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0);

  // 斜纹文字
  const fontSize = Math.max(18, Math.round(canvas.width * 0.028));
  ctx.save();
  ctx.font = `600 ${fontSize}px sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.16)';
  ctx.textBaseline = 'middle';
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((-30 * Math.PI) / 180);
  const stepX = canvas.width * 0.42;
  const stepY = canvas.height * 0.22;
  for (let y = -canvas.height; y <= canvas.height; y += stepY) {
    for (let x = -canvas.width; x <= canvas.width; x += stepX) {
      ctx.fillText(text, x, y);
    }
  }
  ctx.restore();

  // 左下角 logo + 文字
  const pad = Math.round(canvas.width * 0.012);
  const logoSize = Math.round(canvas.width * 0.02);
  let x = pad;
  if (opts.logoUrl) {
    try {
      const logo = await loadImage(opts.logoUrl);
      ctx.globalAlpha = 0.9;
      ctx.drawImage(logo, pad, canvas.height - pad - logoSize, logoSize, logoSize);
      ctx.globalAlpha = 1;
      x = pad + logoSize + 8;
    } catch {
      /* logo 加载失败仅画文字 */
    }
  }
  ctx.font = `500 ${Math.max(14, Math.round(canvas.width * 0.011))}px sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.fillText(text, x, canvas.height - pad - logoSize / 2 + 4);

  return canvas.toDataURL('image/png');
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = src;
  });
}

export function downloadDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
